import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { adminTranslator, DEFAULT_LOCALE, type AdminTranslator, type Locale } from './index.js';

interface I18nContextValue {
  t: AdminTranslator;
  locale: Locale;
  setLocale(locale: Locale): void;
}

const I18nContext = createContext<I18nContextValue | null>(null);

const STORAGE_KEY = 'equinox-admin-locale';

function initialLocale(): Locale {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  const stored = window.localStorage?.getItem(STORAGE_KEY);
  return stored === 'es-MX' || stored === 'en-US' || stored === 'fr-FR' || stored === 'it-IT'
    ? stored
    : DEFAULT_LOCALE;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  const value = useMemo<I18nContextValue>(() => {
    const setLocale = (next: Locale) => {
      setLocaleState(next);
      window.localStorage?.setItem(STORAGE_KEY, next);
    };
    return { t: adminTranslator(locale), locale, setLocale };
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within an I18nProvider');
  return ctx;
}
